import React, { Component } from 'react';
import FormInput from './Input/FormInput';
import SwitcherButton from './Button/SwitcherButton';
import { Data, ValidFields } from '../../shared/Types';
import FileInput from './FileInput/FileInput';

import './Form.scss';

interface FormProps {
  getAllFormValues: (value: boolean, data: Data, cardsData: Data[]) => void;
  avatarSrc?: (fileSrc: string) => void;
}

interface FormState {
  data: Data;
  error: boolean;
}

export class Form extends Component<FormProps, FormState> {
  formValue: React.RefObject<HTMLFormElement>;
  buttonSubmit: React.RefObject<HTMLButtonElement>;
  checkboxRef: React.RefObject<FormInput>;
  isValid: boolean;
  cardsData: Data[];
  fieldsForValidation: ValidFields;

  constructor(props: FormProps) {
    super(props);
    this.formValue = React.createRef();
    this.buttonSubmit = React.createRef();
    this.checkboxRef = React.createRef();
    this.isValid = false;
    this.cardsData = [];

    this.state = {
      data: {
        firstname: '',
        lastname: '',
        country: '',
        date: '',
        agreement: false,
        male: '',
        avatar: '',
      },
      error: false,
    };

    this.fieldsForValidation = {
      lastname: false,
      firstname: false,
      country: false,
      agreement: false,
      date: false,
    };
  }

  isAllInputFieldsValid() {
    return Object.keys(this.fieldsForValidation).every(
      (field: string) => this.fieldsForValidation[field as keyof ValidFields]
    );
  }

  isAllDataValid() {
    if (this.formValue.current) {
      const { firstname, lastname, select, date, agreement, male } = this.formValue.current;

      if (firstname.value.length >= 1) {
        this.fieldsForValidation.firstname = true;
      }

      if (lastname.value.length >= 1) {
        this.fieldsForValidation.lastname = true;
      }

      if (select.value) {
        this.fieldsForValidation.country = true;
      }

      if (agreement.checked) {
        this.fieldsForValidation.agreement = true;
      }

      if (date.value) {
        this.fieldsForValidation.date = true;
      }

      if (this.isAllInputFieldsValid()) {
        this.isValid = true;
        firstname.value = '';
        lastname.value = '';
        agreement.checked = false;
        male.checked = !male.checked;
        select.value = '';
        date.value = '';

        Object.keys(this.fieldsForValidation).map(
          (field: string) => (this.fieldsForValidation[field as keyof ValidFields] = false)
        );

        return true;
      } else {
        if (this.buttonSubmit.current) {
          this.buttonSubmit.current.disabled = true;
        }
        return false;
      }
    }
  }

  handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (this.formValue.current) {
      const { firstname, lastname, select, date, agreement, male } = this.formValue.current;
      this.setState((prevstate) => ({
        data: {
          ...prevstate.data,
          firstname: firstname.value,
          lastname: lastname.value,
          country: select.value,
          date: date.value,
          agreement: agreement.checked,
          male: male.checked ? 'male' : 'female',
        },
      }));
    }
  };

  componentDidMount() {
    if (!this.state.data.country) {
      this.setState((prevstate) => ({ ...prevstate, error: true }));
    }
  }

  componentDidUpdate() {
    if (this.isAllDataValid()) {
      this.cardsData.push(this.state.data);
      if (this.props.getAllFormValues && this.isValid) {
        this.props.getAllFormValues(true, this.state.data, this.cardsData);
      }
    }
  }

  shouldComponentUpdate(nextProp: FormProps, nextState: FormState) {
    if (JSON.stringify(this.state.data) === JSON.stringify(nextState.data)) {
      return false;
    } else return true;
  }

  handleButton = () => {
    if (this.buttonSubmit.current) {
      this.buttonSubmit.current.disabled = false;
    }
  };

  handleAvatarSrc = (src: string) => {
    this.setState((prevstate) => ({
      data: {
        ...prevstate.data,
        avatar: src,
      },
      error: prevstate.error,
    }));
  };

  render() {
    return (
      <div data-testid="form">
        <div className="wrapper">
          <label htmlFor="form">Write your info</label>
          <form
            className="form"
            onSubmit={this.handleSubmit}
            ref={this.formValue}
            onInput={this.handleButton}
          >
            <div className="form__firstname">
              <FormInput
                type="text"
                placeholder="write your first name, more/equal 2 symbols"
                id="firstname"
                className="form__input"
                name="Firstname"
                data-testid="firstname"
              />
            </div>
            <div className="form__lastname">
              <FormInput
                type="text"
                placeholder="write your last name, more/equal 2 symbols"
                id="lastname"
                className="form__input"
                name="Lastname"
                data-testid="lastname"
              />
            </div>

            <div className={this.state.data.country ? 'form__select valid-select' : 'form__select'}>
              <div className="form__data">
                <FormInput
                  type="date"
                  id="date"
                  className="form__select-item date-input"
                  name="Birthday"
                  min="1922-01-01"
                  max={new Date().toISOString().split('T')[0]}
                  defaultChecked={false}
                  data-testid="date"
                />
              </div>

              <select
                className="form__select-item"
                name="select"
                data-testid="country"
                role="select"
              >
                <option value="">--choose country--</option>
                <option role="option" value="belarus">
                  Belarus
                </option>
                <option value="poland">Poland</option>
                <option value="georgia">Georgia</option>
                <option value="russia">Russia</option>
              </select>
              {this.state.error && <span className="input-error">invalid field</span>}
            </div>

            <SwitcherButton />

            <FileInput handleAvatarSrc={this.handleAvatarSrc} />

            <div className="form__agreement">
              <label htmlFor="agreement" className="label form__agreement-label">
                Do you agree with your personal data processing?
              </label>

              <FormInput
                ref={this.checkboxRef}
                type="checkbox"
                id="agreement"
                value="agreement"
                className="agreement-input"
                defaultChecked={false}
                data-testid="agreement"
              />
            </div>

            <button
              type="submit"
              className="form__button"
              ref={this.buttonSubmit}
              disabled={true}
              data-testid="button-submit"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    );
  }
}
